import axios from 'axios'
import type {
  ReturnsAvailableRefundMethodChangeOptionsResponse,
  ReturnsUpdateReturnProcessRequest
} from '@itsrever/rever-api'

// Not a refund method: the exchange is the compensation being replaced.
export const EXCHANGE_METHOD = 'EXCHANGE'

type Options = ReturnsAvailableRefundMethodChangeOptionsResponse | undefined

export function currentMethod(options: Options): string | undefined {
  return options?.from_exchange ? EXCHANGE_METHOD : options?.from
}

export function selectableMethods(options: Options): string[] {
  return [currentMethod(options), ...(options?.to ?? [])].filter(
    (method): method is string => !!method
  )
}

const NO_BLOCKER = 'EXCHANGE_TO_OPM_BLOCKER_NONE'

// A mixed return keeps its refund lines editable; only an exchange-only return has nothing else to edit.
export function isExchangeChangeBlocked(
  options: Options,
  exchangeOnly: boolean
): boolean {
  const blocker = options?.exchange_to_opm_blocker
  return (
    exchangeOnly &&
    (options?.to ?? []).length === 0 &&
    !!blocker &&
    blocker !== NO_BLOCKER
  )
}

// An exchange-only return has no refund to adjust: the only valid change is to a refund method.
export function keepsExchangeOnly(
  options: Options,
  form: { method: string },
  exchangeOnly: boolean
): boolean {
  return (
    exchangeOnly &&
    options?.from_exchange === true &&
    form.method === EXCHANGE_METHOD
  )
}

export function toUpdateRequest(
  options: Options,
  form: { method: string }
): Pick<ReturnsUpdateReturnProcessRequest, 'refund_method' | 'from_exchange'> {
  type RefundMethod = ReturnsUpdateReturnProcessRequest['refund_method']
  // Keeping the exchange is a plain amount adjustment on the lines' own method.
  if (form.method === EXCHANGE_METHOD) {
    return { refund_method: options?.from as RefundMethod }
  }
  const refundMethod = form.method as RefundMethod
  if (options?.from_exchange) {
    return { refund_method: refundMethod, from_exchange: true }
  }
  return { refund_method: refundMethod }
}

// The backend gives up waiting after a while (HTTP 504) but the change may still complete.
export function isStillApplying(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 504
}
